import { ActionIcon, Badge, Button, Group, Modal, Select, Stack, Switch, Table, Tabs, Text, TextInput, Title } from "@mantine/core";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { type Schemas, api, call, hasPermission } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { ErrorAlert, Loading, notifyError, notifyOk } from "../../components/common";

type Reason = Schemas["ReasonOut"];
type Kind = "downtime" | "reject";

export const CATEGORY_LABELS: Record<string, { label: string; color: string; help: string }> = {
  breakdown: { label: "Breakdown", color: "red", help: "Unplanned failure (availability loss, feeds MTBF/MTTR)" },
  setup: { label: "Setup", color: "blue", help: "Setup/changeover (availability loss unless set as planned in KPI settings)" },
  material: { label: "Material", color: "orange", help: "Waiting for material or logistics (availability loss)" },
  unplanned: { label: "Unplanned", color: "yellow", help: "Other unplanned stop (availability loss)" },
  planned: { label: "Planned", color: "gray", help: "Planned stop: excluded from planned production time" },
  other: { label: "Other", color: "grape", help: "Unclassified (availability loss)" },
  scrap: { label: "Scrap", color: "red", help: "Rejected part (quality loss)" },
  rework: { label: "Rework", color: "orange", help: "Part needs rework (quality loss unless rework counts as good)" },
};
const KIND_CATEGORIES: Record<Kind, string[]> = {
  downtime: ["breakdown", "setup", "material", "unplanned", "planned", "other"],
  reject: ["scrap", "rework"],
};

/** Depth-first order with depth, so a flat list renders as a tree. */
export function asTree(reasons: Reason[]): { r: Reason; depth: number }[] {
  const children = new Map<string | null, Reason[]>();
  for (const r of reasons) children.set(r.parent_id ?? null, [...(children.get(r.parent_id ?? null) ?? []), r]);
  const out: { r: Reason; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const r of (children.get(parent) ?? []).sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code))) {
      out.push({ r, depth });
      walk(r.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function ReasonCodesPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const canManage = hasPermission(me, "masterdata:manage");
  const [kind, setKind] = useState<Kind>("downtime");
  const [editing, setEditing] = useState<{ parent?: Reason; reason?: Reason } | null>(null);
  const reasons = useQuery({
    queryKey: ["reason-codes", "all"],
    queryFn: () => call(api.GET("/api/v1/reason-codes", { params: { query: { include_inactive: true } } })),
  });
  const importDefaults = useMutation({
    mutationFn: () => call(api.POST("/api/v1/reason-codes/import-defaults")),
    onSuccess: () => { notifyOk("Default reasons added"); void qc.invalidateQueries({ queryKey: ["reason-codes"] }); },
    onError: notifyError,
  });

  if (reasons.isLoading) return <Loading />;
  if (reasons.error) return <ErrorAlert error={reasons.error} />;
  const tree = asTree(reasons.data!.filter((r) => r.kind === kind));
  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={2}>Reason codes</Title>
          <Text size="sm" c="dimmed">Your own reasons for stops and rejects. Each top-level reason belongs to a standard category, which drives the KPIs.</Text>
        </div>
        {canManage && (
          <Group gap="xs">
            <Button variant="default" loading={importDefaults.isPending} onClick={() => importDefaults.mutate()}>Add missing defaults</Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing({})}>New reason</Button>
          </Group>
        )}
      </Group>
      <Tabs value={kind} onChange={(v) => setKind((v ?? "downtime") as Kind)}>
        <Tabs.List>
          <Tabs.Tab value="downtime">Downtime</Tabs.Tab>
          <Tabs.Tab value="reject">Rejects</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      <Table striped>
        <Table.Thead>
          <Table.Tr><Table.Th>Reason</Table.Th><Table.Th>Code</Table.Th><Table.Th>Category</Table.Th><Table.Th /></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tree.map(({ r, depth }) => (
            <Table.Tr key={r.id} opacity={r.is_active ? 1 : 0.5}>
              <Table.Td style={{ paddingLeft: 12 + depth * 24 }}>
                {r.name} {!r.is_active && <Badge size="xs" color="gray">inactive</Badge>}
              </Table.Td>
              <Table.Td><Text size="sm" ff="monospace">{r.code}</Text></Table.Td>
              <Table.Td>
                <Badge variant="light" color={CATEGORY_LABELS[r.category]?.color} title={CATEGORY_LABELS[r.category]?.help}>
                  {CATEGORY_LABELS[r.category]?.label ?? r.category}
                </Badge>
              </Table.Td>
              <Table.Td>
                {canManage && (
                  <Group gap={4} justify="end">
                    {depth < 2 && (
                      <ActionIcon variant="subtle" aria-label={`Add sub-reason to ${r.name}`} onClick={() => setEditing({ parent: r })}>
                        <IconPlus size={16} />
                      </ActionIcon>
                    )}
                    <ActionIcon variant="subtle" aria-label={`Edit ${r.name}`} onClick={() => setEditing({ reason: r })}>
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Group>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {tree.length === 0 && <Text size="sm" c="dimmed">No reasons yet.</Text>}
      <Modal opened={editing !== null} onClose={() => setEditing(null)}
        title={editing?.reason ? "Edit reason" : editing?.parent ? `New sub-reason of ${editing.parent.name}` : "New reason"}>
        {editing && <ReasonForm kind={kind} {...editing} onDone={() => setEditing(null)} />}
      </Modal>
    </Stack>
  );
}

function ReasonForm({ kind, parent, reason, onDone }: { kind: Kind; parent?: Reason; reason?: Reason; onDone: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState(reason?.name ?? "");
  const [category, setCategory] = useState<string | null>(reason?.category ?? parent?.category ?? KIND_CATEGORIES[kind][0]);
  const [active, setActive] = useState(reason?.is_active ?? true);
  const topLevel = reason ? reason.parent_id === null : !parent;
  const save = useMutation({
    mutationFn: () => reason
      ? call(api.PATCH("/api/v1/reason-codes/{reason_id}", {
          params: { path: { reason_id: reason.id } },
          body: { name, is_active: active, ...(topLevel ? { category: category as Schemas["ReasonUpdate"]["category"] } : {}) },
        }))
      : call(api.POST("/api/v1/reason-codes", {
          body: { kind, code, name, parent_id: parent?.id ?? null,
            ...(topLevel ? { category: category as Schemas["ReasonCreate"]["category"] } : {}) },
        })),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["reason-codes"] }); onDone(); },
    onError: notifyError,
  });
  return (
    <Stack>
      {!reason && <TextInput label="Code" required value={code} onChange={(e) => setCode(e.currentTarget.value)} />}
      <TextInput label="Name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
      <Select label="Category" disabled={!topLevel}
        description={topLevel ? CATEGORY_LABELS[category ?? ""]?.help : "Sub-reasons use the category of their top-level reason"}
        data={KIND_CATEGORIES[kind].map((c) => ({ value: c, label: CATEGORY_LABELS[c].label }))}
        value={category} onChange={setCategory} />
      {reason && <Switch label="Active" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />}
      <Button disabled={!name || (!reason && !code)} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
    </Stack>
  );
}
